/**
 * Per-thread draft persistence for the GlobalChatSystem composer.
 *
 * Backs Fix 1: composer text + selected (but not yet sent) attachments
 * are persisted PER THREAD, keyed by the active thread id.
 *
 * Storage strategy
 * ────────────────
 *  • Text draft  → key  `prest0n:chat:draft:<threadId>`
 *      payload: { html, text }
 *  • Attachment metadata draft → key  `prest0n:chat:draft-att:<threadId>`
 *      payload: AttachmentDraftEntry[]
 *
 * Drafts MUST survive: thread switch, SPA navigation, page refresh,
 * logout/login, widget close/reopen. localStorage is therefore the
 * only acceptable backing store (sessionStorage dies on tab close,
 * memory dies on refresh).
 *
 * Each draft is hard-capped at MAX_DRAFT_BYTES (50 KB) so a runaway
 * paste doesn't spam quota. Attachment payloads embed base64 dataUrl
 * only for files <2 MB; larger files persist metadata only and the
 * caller is expected to surface a "re-attach <filename>" affordance.
 *
 * All reads/writes are wrapped in try/catch so private-mode browsers,
 * disabled storage, and quota exceptions never throw into React.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

const DRAFT_KEY_PREFIX = 'prest0n:chat:draft:';
const ATT_KEY_PREFIX = 'prest0n:chat:draft-att:';

// ============================================================================
// IndexedDB-backed attachment blob store
// ============================================================================
//
// localStorage caps out at ~5 MB per origin, with each draft hard-capped
// at 50 KB. That's fine for text but blows up the moment a user attaches a
// real PDF or photo and then switches threads / refreshes / logs out and
// back in. The previous strategy turned anything over 2 MB into a
// metadata-only "placeholder" requiring re-attach — silent foot-gun.
//
// The IDB store below persists the raw File blobs keyed by thread id so
// real attachments survive every navigation pattern. localStorage stays
// in play for the metadata index (small) and for fallback-only when IDB
// is unavailable (private mode, ancient browsers).

const IDB_DB_NAME = 'prest0n_chat_drafts';
const IDB_DB_VERSION = 1;
const IDB_ATTACHMENT_STORE = 'attachments';

interface IdbAttachmentRecord {
  id: string;            // `${threadId}::${index}`
  threadId: string;
  order: number;
  name: string;
  type: string;
  size: number;
  lastModified?: number;
  blob: Blob;
}

let idbDbPromise: Promise<IDBDatabase | null> | null = null;

function openIdbDatabase(): Promise<IDBDatabase | null> {
  if (idbDbPromise) return idbDbPromise;
  if (typeof indexedDB === 'undefined') {
    idbDbPromise = Promise.resolve(null);
    return idbDbPromise;
  }
  idbDbPromise = new Promise(resolve => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, IDB_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_ATTACHMENT_STORE)) {
          const store = db.createObjectStore(IDB_ATTACHMENT_STORE, { keyPath: 'id' });
          store.createIndex('threadId', 'threadId', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return idbDbPromise;
}

async function idbWriteAttachments(threadId: string, files: File[]): Promise<boolean> {
  const db = await openIdbDatabase();
  if (!db) return false;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_ATTACHMENT_STORE, 'readwrite');
      const store = tx.objectStore(IDB_ATTACHMENT_STORE);
      const idx = store.index('threadId');
      const cursorReq = idx.openCursor(IDBKeyRange.only(threadId));
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          // After clearing existing entries, write the new ones.
          files.forEach((file, i) => {
            const record: IdbAttachmentRecord = {
              id: `${threadId}::${i}`,
              threadId,
              order: i,
              name: file.name,
              type: file.type || 'application/octet-stream',
              size: file.size,
              lastModified: file.lastModified,
              blob: file,
            };
            try { store.put(record); } catch { /* ignore */ }
          });
        }
      };
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

async function idbReadAttachments(threadId: string): Promise<File[]> {
  const db = await openIdbDatabase();
  if (!db) return [];
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_ATTACHMENT_STORE, 'readonly');
      const store = tx.objectStore(IDB_ATTACHMENT_STORE);
      const idx = store.index('threadId');
      const records: IdbAttachmentRecord[] = [];
      const req = idx.openCursor(IDBKeyRange.only(threadId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          records.push(cursor.value as IdbAttachmentRecord);
          cursor.continue();
        }
      };
      tx.oncomplete = () => {
        records.sort((a, b) => a.order - b.order);
        const files = records.map(r => {
          try {
            return new File([r.blob], r.name, {
              type: r.type || 'application/octet-stream',
              lastModified: r.lastModified || Date.now(),
            });
          } catch {
            return null;
          }
        }).filter(Boolean) as File[];
        resolve(files);
      };
      tx.onerror = () => resolve([]);
      tx.onabort = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

async function idbClearAttachments(threadId: string): Promise<void> {
  const db = await openIdbDatabase();
  if (!db) return;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_ATTACHMENT_STORE, 'readwrite');
      const store = tx.objectStore(IDB_ATTACHMENT_STORE);
      const idx = store.index('threadId');
      const cursorReq = idx.openCursor(IDBKeyRange.only(threadId));
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function idbListThreadIds(): Promise<string[]> {
  const db = await openIdbDatabase();
  if (!db) return [];
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_ATTACHMENT_STORE, 'readonly');
      const store = tx.objectStore(IDB_ATTACHMENT_STORE);
      const idx = store.index('threadId');
      const ids = new Set<string>();
      const req = idx.openKeyCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          ids.add(String(cursor.key));
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve(Array.from(ids));
      tx.onerror = () => resolve([]);
      tx.onabort = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

export async function loadAttachmentDraftBlobs(threadId: string): Promise<File[]> {
  if (!threadId) return [];
  return idbReadAttachments(threadId);
}

/** 50 KB cap per draft payload (text + attachment-metadata each). */
export const MAX_DRAFT_BYTES = 50 * 1024;

/** Files smaller than this are embedded as base64 dataUrl in the draft. */
export const MAX_INLINE_ATTACHMENT_BYTES = 2 * 1024 * 1024;

export interface DraftPayload {
  html: string;
  text: string;
}

export interface AttachmentDraftEntry {
  name: string;
  type: string;
  size: number;
  lastModified?: number;
  /** Base64 data URL when file <2 MB, undefined otherwise (placeholder). */
  dataUrl?: string;
}

/** Build the localStorage key for a thread's text draft. */
export function draftKey(threadId: string): string {
  return `${DRAFT_KEY_PREFIX}${threadId}`;
}

/** Build the localStorage key for a thread's attachment-metadata draft. */
export function attachmentDraftKey(threadId: string): string {
  return `${ATT_KEY_PREFIX}${threadId}`;
}

/** Cheap byte-length estimate (UTF-16 surrogates aside). */
function approxByteLength(s: string): number {
  // localStorage values are stored as UTF-16; budget conservatively as
  // the JSON char length. A precise byte count isn't required — we
  // just need to refuse pathological payloads (10 MB pasted images).
  return s.length;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Persist a text draft for the given thread. Empty payloads remove the
 * key so stale drafts don't bloat storage.
 */
export function saveDraft(threadId: string, payload: DraftPayload): void {
  if (!threadId) return;
  const empty = !payload || (!payload.html && !payload.text);
  if (empty) {
    safeRemoveItem(draftKey(threadId));
    return;
  }
  let serialized: string;
  try {
    serialized = JSON.stringify(payload);
  } catch {
    return;
  }
  if (approxByteLength(serialized) > MAX_DRAFT_BYTES) {
    // Drop the HTML half first (keeps the user's plain text typed so far)
    // then truncate. This degrades gracefully on a 10 MB rich-text paste
    // instead of silently dropping the whole draft.
    const truncated: DraftPayload = {
      text: (payload.text || '').slice(0, MAX_DRAFT_BYTES),
      html: '',
    };
    try {
      serialized = JSON.stringify(truncated);
    } catch {
      return;
    }
  }
  safeSetItem(draftKey(threadId), serialized);
}

/** Load a thread's text draft, or null if absent / corrupt. */
export function loadDraft(threadId: string): DraftPayload | null {
  if (!threadId) return null;
  const raw = safeGetItem(draftKey(threadId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        html: typeof parsed.html === 'string' ? parsed.html : '',
        text: typeof parsed.text === 'string' ? parsed.text : '',
      };
    }
  } catch {
    // ignore
  }
  return null;
}

/** Clear a thread's text draft. */
export function clearDraft(threadId: string): void {
  if (!threadId) return;
  safeRemoveItem(draftKey(threadId));
}

/**
 * Build an AttachmentDraftEntry from a File. For files smaller than
 * MAX_INLINE_ATTACHMENT_BYTES the resolved entry includes a base64
 * dataUrl so the attachment can be rehydrated on full reload. Larger
 * files resolve as metadata-only placeholders.
 */
export function fileToDraftEntry(file: File): Promise<AttachmentDraftEntry> {
  return new Promise((resolve) => {
    const baseEntry: AttachmentDraftEntry = {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
    };
    if (!file.size || file.size > MAX_INLINE_ATTACHMENT_BYTES) {
      resolve(baseEntry);
      return;
    }
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : undefined;
        resolve({ ...baseEntry, dataUrl: result });
      };
      reader.onerror = () => resolve(baseEntry);
      reader.readAsDataURL(file);
    } catch {
      resolve(baseEntry);
    }
  });
}

/**
 * Persist a list of selected-but-not-yet-sent attachments for the
 * given thread. Each File is serialized to an AttachmentDraftEntry
 * (with dataUrl when small enough); if the serialized payload would
 * exceed MAX_DRAFT_BYTES we drop dataUrls greedily, largest first,
 * until the payload fits. Empty lists clear the key.
 */
export async function saveAttachmentDraft(
  threadId: string,
  files: File[],
): Promise<void> {
  if (!threadId) return;
  if (!files || files.length === 0) {
    safeRemoveItem(attachmentDraftKey(threadId));
    return;
  }
  let entries: AttachmentDraftEntry[];
  try {
    entries = await Promise.all(files.map(fileToDraftEntry));
  } catch {
    return;
  }
  let serialized = '';
  try {
    serialized = JSON.stringify(entries);
  } catch {
    return;
  }
  if (approxByteLength(serialized) > MAX_DRAFT_BYTES) {
    // Drop dataUrls largest-first until the payload fits, leaving the
    // metadata so the chip + re-attach affordance still renders.
    const indexed = entries
      .map((e, i) => ({ idx: i, size: e.dataUrl?.length || 0 }))
      .sort((a, b) => b.size - a.size);
    for (const { idx } of indexed) {
      if (!entries[idx].dataUrl) continue;
      entries[idx] = { ...entries[idx], dataUrl: undefined };
      try {
        serialized = JSON.stringify(entries);
      } catch {
        return;
      }
      if (approxByteLength(serialized) <= MAX_DRAFT_BYTES) break;
    }
  }
  if (approxByteLength(serialized) > MAX_DRAFT_BYTES) {
    // Still too large — keep names only.
    const stripped = entries.map(e => ({
      name: e.name,
      type: e.type,
      size: e.size,
      lastModified: e.lastModified,
    }));
    try {
      serialized = JSON.stringify(stripped);
    } catch {
      return;
    }
  }
  safeSetItem(attachmentDraftKey(threadId), serialized);
}

/** Load a thread's attachment-metadata draft, or [] if absent / corrupt. */
export function loadAttachmentDraft(threadId: string): AttachmentDraftEntry[] {
  if (!threadId) return [];
  const raw = safeGetItem(attachmentDraftKey(threadId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((e: any) => e && typeof e.name === 'string')
        .map((e: any) => ({
          name: String(e.name),
          type: typeof e.type === 'string' ? e.type : '',
          size: typeof e.size === 'number' ? e.size : 0,
          lastModified: typeof e.lastModified === 'number' ? e.lastModified : undefined,
          dataUrl: typeof e.dataUrl === 'string' ? e.dataUrl : undefined,
        }));
    }
  } catch {
    // ignore
  }
  return [];
}

/** Clear a thread's attachment-metadata draft. */
export function clearAttachmentDraft(threadId: string): void {
  if (!threadId) return;
  safeRemoveItem(attachmentDraftKey(threadId));
  void idbClearAttachments(threadId);
}

/**
 * Decode a draft entry's base64 dataUrl back into a File so it can be
 * placed into `state.attachments`. Returns null when no dataUrl is
 * present (the caller should render the entry as a re-attach
 * placeholder instead).
 */
export function draftEntryToFile(entry: AttachmentDraftEntry): File | null {
  if (!entry || !entry.dataUrl) return null;
  try {
    const commaIdx = entry.dataUrl.indexOf(',');
    if (commaIdx === -1) return null;
    const meta = entry.dataUrl.slice(0, commaIdx);
    const data = entry.dataUrl.slice(commaIdx + 1);
    const isBase64 = /;base64$/i.test(meta) || /;base64;/.test(meta);
    let bytes: Uint8Array;
    if (isBase64) {
      const binary = atob(data);
      bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    } else {
      const decoded = decodeURIComponent(data);
      bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
    }
    return new File([bytes], entry.name, {
      type: entry.type || 'application/octet-stream',
      lastModified: entry.lastModified || Date.now(),
    });
  } catch {
    return null;
  }
}

/**
 * Sweep stale draft keys whose threadId no longer exists in the user's
 * thread list. Called once on chat-system mount per Fix 1's
 * storage-hygiene requirement.
 */
export function sweepStaleDraftKeys(activeThreadIds: Iterable<string>): void {
  const valid = new Set<string>();
  for (const id of activeThreadIds) {
    if (id) valid.add(id);
  }
  // Sweep IDB attachment blobs for retired threads (async, fire-and-forget).
  void (async () => {
    try {
      const ids = await idbListThreadIds();
      for (const id of ids) {
        if (!valid.has(id)) {
          await idbClearAttachments(id);
        }
      }
    } catch { /* ignore */ }
  })();
  if (typeof localStorage === 'undefined') return;
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(DRAFT_KEY_PREFIX) || k.startsWith(ATT_KEY_PREFIX))) {
        keys.push(k);
      }
    }
  } catch {
    return;
  }
  for (const k of keys) {
    const id = k.startsWith(DRAFT_KEY_PREFIX)
      ? k.slice(DRAFT_KEY_PREFIX.length)
      : k.slice(ATT_KEY_PREFIX.length);
    if (!valid.has(id)) {
      safeRemoveItem(k);
    }
  }
}
